export type AreaKind = "area" | "part";

/** Who made the area: Tina's one-off cleanup, its nightly, or a curator in IMS. */
export type AreaOrigin = "cleanup" | "auto" | "curator" | "";

export type LatLng = { latitude: number; longitude: number };

/** A list row (no shape): an `/areas/explore` hit mapped by `toAreaListItem`. */
export interface AreaListItem {
  id: string;
  city: string;
  countryCode: string;
  name: string;
  kind: AreaKind;
  parentId: string | null;
  partOrder: number | null;
  origin: AreaOrigin;
  venueCount: number;
  aliases: string[];
  displayNames: Record<string, string>;
  centre: LatLng | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * The Typesense document of `GET /areas/explore`. Optional fields are absent when unset, `centre`
 * is `[lat, lng]` and the dates are unix seconds.
 */
export interface AreaSummary {
  id: string;
  name: string;
  city: string;
  countryCode: string;
  cityKey: string;
  kind: AreaKind;
  parentId?: string;
  hasParent: boolean;
  origin?: AreaOrigin;
  venueCount: number;
  partOrder?: number;
  aliases?: string[];
  displayNames?: Record<string, string>;
  centre?: [number, number];
  createdAt?: number;
  updatedAt?: number;
}

/** One hit of `/areas/explore`, the way the default data provider returns it. */
export interface AreaHit {
  document: AreaSummary;
}

/** The full document (`GET /areas/{id}`, `GET /areas?city=`): the list fields plus the polygon. */
export interface Area extends AreaListItem {
  /** Empty until the nightly writes one for a curator-created area. */
  shape: LatLng[];
  aliasKeys: string[];
  shapeHash?: string | null;
}

export interface AreaTreeNode {
  id: string;
  name: string;
  kind: AreaKind;
  displayNames: Record<string, string>;
  aliases: string[];
  aliasKeys: string[];
  centre: LatLng | null;
  venueCount: number;
}

export interface AreaTreePart {
  part: AreaTreeNode;
  totalVenueCount: number;
  children: AreaTreeNode[];
}

export interface AreaTree {
  parts: AreaTreePart[];
  unparented: AreaTreeNode[];
}

/** Serialised as the enum name (`JsonStringEnumConverter` on `VenueStatusDb`), not a number. */
export type AreaVenueStatus = "Requested" | "Unregistered" | "Complete";

export interface AreaVenueItem {
  id: string;
  name: string;
  status: AreaVenueStatus;
  hide: boolean;
  area: string | null;
  geolocation: LatLng;
}

export interface CreateAreaResponse {
  area: Area;
  created: boolean;
}

export interface RemoveAreaAliasResponse {
  area: Area;
  removed: boolean;
}

export interface MergeAreaResponse {
  area: Area;
  movedVenues: number;
  remaining: number;
}

export interface SyncAreaVenuesResponse {
  updated: number;
  remaining: number;
}

export interface CreateAreaBody {
  city: string;
  countryCode: string;
  name: string;
  kind?: AreaKind;
  parentId?: string;
  aliases?: string[];
  displayNames?: Record<string, string>;
  partOrder?: number;
  origin: AreaOrigin;
}

/** Partial update. `displayNames` replaces the whole map; there is no way to clear a part. */
export interface UpdateAreaBody {
  name?: string;
  keepOldNameAsAlias?: boolean;
  displayNames?: Record<string, string>;
  parentId?: string;
  partOrder?: number;
  origin?: AreaOrigin;
}

/**
 * 409 body (`GenericApiHandlers.ConflictResponse`). `childCount` is only present when a part
 * with child areas refuses a merge, and then `existingId` is that part's own id.
 */
export interface AreaConflictResponse {
  isSuccess: false;
  message: string;
  existingId: string;
  childCount?: number;
}

export const cityKey = (row: { city: string; countryCode: string }): string =>
  `${row.city}|${row.countryCode}`;

/** Inverse of {@link cityKey}; splits on the last `|` so a city may never confuse it. */
export const parseCityKey = (
  key: string,
): { city: string; countryCode: string } => {
  const i = key.lastIndexOf("|");
  return i < 0
    ? { city: key, countryCode: "" }
    : { city: key.slice(0, i), countryCode: key.slice(i + 1) };
};
